import { useMsal } from '@azure/msal-react';
import { addMinutes, compareAsc, fromUnixTime } from 'date-fns';
import { useMemo, useState, useEffect, useCallback, createContext } from 'react';

import { MSAL_SCOPES } from 'src/utils/constants';

import { useGetUserId } from './hooks/useGetUserId';
import { useGetTenants } from './hooks/useGetTenants';
import { useCreateSession } from './hooks/useCreateSession';

import type { User, UserRoles } from './types';

type TAuthContext = {
  user: User | undefined;
  setUser: React.Dispatch<React.SetStateAction<User | undefined>>;
};

type AuthContextProviderProps = {
  children: React.ReactNode;
};

export const AuthContext = createContext<TAuthContext | undefined>(undefined);

function AuthProvider({ children }: AuthContextProviderProps) {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isAuthTokenSet, setIsAuthTokenSet] = useState(false);
  const [tokenExpiration, setTokenExpiration] = useState<Date | null>(null);
  const { accounts, instance } = useMsal();
  const { idToken, name, idTokenClaims } = accounts[0] || {};
  const { id: userId } = useGetUserId(isAuthTokenSet);
  const { tenants } = useGetTenants(isAuthTokenSet);
  const createSession = useCreateSession();

  const setToken = useCallback((newIdToken: string | undefined, expirationDate: Date | null) => {
    localStorage.setItem('idToken', newIdToken || '');
    setTokenExpiration(expirationDate); // update with the expiration date of the last token
    setIsAuthTokenSet(true);
  }, []);

  const refreshAuthToken = useCallback(async () => {
    if (accounts.length) {
      try {
        const response = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: MSAL_SCOPES,
          forceRefresh: true, // This is required since the idToken always expires early
        });
        const newExpiration = (response.idTokenClaims as { exp: number }).exp;
        setToken(response.idToken, fromUnixTime(newExpiration));
      } catch (error) {
        console.error('Failed to refresh token:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, instance]);

  // Set Axios Authorization header initially
  useEffect(() => {
    if (idToken && idTokenClaims?.exp) {
      setToken(idToken, fromUnixTime(idTokenClaims.exp));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, idTokenClaims]);

  /*
   * This effect handles token refresh logic to ensure the user remains authenticated.
   *
   * - Adds an event listener to refresh the token when the browser window gains focus and toke is close to expire.
   * - Sets up a periodic interval to refresh the token every 45 minutes.
   * - Cleans up the interval and event listener when the component unmounts.
   */
  useEffect(() => {
    const refreshOnFocus = () => {
      if (idToken && tokenExpiration) {
        const refreshTime = addMinutes(new Date(), 15); // to check 15 minutes before expiration

        if (compareAsc(refreshTime, tokenExpiration) > 0) {
          refreshAuthToken();
        }
      }
    };

    window.addEventListener('focus', refreshOnFocus);

    const interval = setInterval(
      () => {
        refreshAuthToken();
      },
      45 * 60 * 1000
    );

    return () => {
      clearInterval(interval); // Cleanup on unmount
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [idToken, refreshAuthToken, tokenExpiration]);

  useEffect(() => {
    if (accounts.length) {
      const account = accounts[0];
      const fullName = name;
      const firstName = fullName?.split(' ')[0];
      const lastName = fullName?.split(' ').at(-1);
      setUser({
        firstName,
        lastName,
        id: userId,
        roles: account.idTokenClaims?.roles as UserRoles[] | undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, userId]);

  useEffect(() => {
    // Create a session
    if (userId && tenants?.length) {
      const tenantId = tenants.find((tenant: { name: string }) => tenant.name === 'default')?.id;
      if (tenantId) {
        createSession({ user_id: userId, tenant_id: tenantId });
      }
    }
  }, [userId, tenants, createSession]);

  const values = useMemo(() => ({ user, setUser }), [user]);

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
